import {Injectable} from '@angular/core';
import {ApiFileResponse} from '../http/api-file-response';
import {from, map, mergeMap, Observable} from 'rxjs';
import {Measurement} from '../data/measurement';
import {Utils} from '@astek/utils';
import * as JSZip from 'jszip';
import {MeanPressureComputationService} from './mean-pressure-computation.service';

@Injectable()
export class FileParserService {

  constructor(
    private readonly _meanPressureComputationService: MeanPressureComputationService,
  ) {
  }

  public parsePressureFileContent(fileResponse: ApiFileResponse): Observable<Array<Measurement>> {
    this._meanPressureComputationService.clearPressureData();
    const rawZippedContent = this.base64ToArrayBuffer(fileResponse.contentBase64);
    return from(crypto.subtle.digest('SHA-256', rawZippedContent)).pipe(
      mergeMap((zipContentHash: ArrayBuffer) => {
        if (Utils.arrayBufferToString(zipContentHash) !== fileResponse.checksum) {
          throw new Error('Corrupted zip content');
        }
        const zipper = new JSZip();
        return from(zipper.loadAsync(rawZippedContent));
      }),
      mergeMap((zip: JSZip) => {
        const fileList = new Array<string>();
        zip.forEach((path) => {
          fileList.push(path);
        });
        if (fileList.length !== 1) {
          throw new Error('Zip content different from expected');
        }
        return from(zip.file(fileList[0])!.async('text'));
      }),
      map((rawFileContent) => {
        const lines = rawFileContent.split('\n');
        if (lines.length < 3) {
          throw new Error('Missing pressure data');
        }
        const measurements: Array<Measurement> = [];
        for (let i = 2; i < lines.length; i++) {
          const columns = lines[i].split(',');
          if (columns.length >= 2) {
            const rawPressure = Number.parseFloat(columns[1]);
            measurements.push(new Measurement(
              Number.parseFloat(columns[0]),
              rawPressure,
              this._meanPressureComputationService.computeCurrentMeanPressure(rawPressure),
            ));
          }
        }
        return measurements;
      }),
    );
  }

  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binaryString = window.atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);

    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    return bytes.buffer;
  }

}
